import * as React from 'react'; // eslint-disable-line
import styled from 'styled-components';
import { TransformComponent as ZoomableContent } from 'react-zoom-pan-pinch';

import { PreviewPage } from './preview-page';
import { PageDataProps } from './preview';

export interface PreviewContentProps {
    /**
     * Page data
     */
    pageData: PageDataProps[];
    /**
     * Scroll listener
     */
    scrollListener: (e: any) => void;
    /**
     * Scroll ref
     */
    scrollRef: React.RefObject<HTMLDivElement>;
    /**
     * Show navigation?
     */
    showNavigation: boolean;
    /**
     * Fixes the size of the page during loading.
     */
    isFixedSize: boolean;
    /**
     * Show fullscreen capability?
     */
    hasFullscreen: boolean;
    /**
     * Don't show the invisible page at bottom
     */
    isSinglePage: boolean;
    /**
     * Limit how wide a page is allowed to be
     */
    maxPageWidth?: number;
    /**
     * Is the display currently fullscreen
     */
    isFullscreen: boolean;
    /**
     * Should the pages be landscape or portrait?
     */
    isPortrait: boolean;
}

interface PageWrapperProps {
    showNavigation: boolean;
    hasFullscreen: boolean;
    isFullscreen: boolean;
    isSinglePage: boolean;
    isPortrait: boolean;
}

const PageWrapper = styled.div<PageWrapperProps>`
    box-sizing: border-box;
    position: relative;
    height: ${(props) => (props.hasFullscreen ? (props.isFullscreen ? '100%' : 'calc(100% - 44px)') : '100%')};
    overflow-y: ${(props) => (props.isSinglePage ? 'auto' : 'scroll')};
    width: ${(props) => (props.isSinglePage ? '100%' : 'fit-content')};
    display: ${(props) => (props.hasFullscreen && props.isSinglePage ? 'flex' : 'block')};
    ${(props) =>
        props.hasFullscreen &&
        (props.isSinglePage || props.isPortrait) &&
        `align-items: ${props.isPortrait} ? 'flex-start' : 'center'`}
    cursor: grab;
`;

const Pages = styled.ul`
    margin: 0 auto;
    max-width: 100vw;
    width: 100%;
    padding: 6px;
`;

const LastPage = styled.li`
    height: 60vh;
    max-width: 100vw;
    margin: 0 auto;
    width: 100%;
`;

export const PreviewContent = ({
    showNavigation,
    hasFullscreen,
    isFullscreen,
    isSinglePage,
    isFixedSize,
    isPortrait,
    maxPageWidth,
    scrollRef,
    scrollListener,
    pageData,
}: PreviewContentProps) => {
    return (
        <PageWrapper
            showNavigation={showNavigation}
            hasFullscreen={hasFullscreen}
            isFullscreen={isFullscreen}
            isSinglePage={isSinglePage}
            id={'preview__scroll-container'}
            ref={scrollRef}
            onScroll={(e: any) => scrollListener(e)}
            isPortrait={isPortrait}
        >
            <ZoomableContent wrapperStyle={{ width: '100%' }}>
                <Pages>
                    {pageData.map((page: PageDataProps, index: number) => (
                        <PreviewPage
                            key={`page-${page.id}`}
                            id={page.id}
                            isFixedSize={isFixedSize}
                            canBeFullscreen={hasFullscreen}
                            isLoading={page.isLoading ?? false}
                            isPortrait={isPortrait}
                            maxWidth={maxPageWidth}
                            title={page.title}
                            index={index}
                            pageType={page.pageType}
                            content={page.content}
                        />
                    ))}
                    {!isSinglePage && <LastPage />}
                </Pages>
            </ZoomableContent>
        </PageWrapper>
    );
};
PreviewContent.displayName = 'PreviewContent';
